import React, { useState, useEffect } from 'react';
import { Plus, Tag, X, Calendar, Clock } from 'lucide-react';
import { api } from '../../services/api';
import './AdminUI.css';
import AdminTopbarPortal from './AdminTopbarPortal';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

export default function AdminCoupons() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [updatingCouponId, setUpdatingCouponId] = useState(null);
  const [statusError, setStatusError] = useState('');
  const [form, setForm] = useState({
    code: '',
    type: 'percentage',
    discount: '',
    minOrder: '0',
    description: '',
    usageLimit: '',
    expiresAt: '',
    always: true,
    dateRange: { start: '', end: '' },
    daysOfWeek: [],
    applicableItems: [],
    applicableCategories: []
  });
  const [error, setError] = useState('');

  const load = async () => {
    try {
      const [couponData, catData, itemData] = await Promise.all([
        api.getCoupons(),
        api.getCategories(),
        api.getMenuItems()
      ]);
      setCoupons(couponData);
      setCategories(catData);
      setMenuItems(itemData);
    } catch (err) {
      console.error('Failed to load:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openForm = () => {
    setForm({
      code: '',
      type: 'percentage',
      discount: '',
      minOrder: '0',
      description: '',
      usageLimit: '',
      expiresAt: '',
      always: true,
      dateRange: { start: '', end: '' },
      daysOfWeek: [],
      applicableItems: [],
      applicableCategories: []
    });
    setShowForm(true);
    setError('');
  };

  const handleSave = async () => {
    if (!form.code || !form.discount) {
      setError('Code and discount are required');
      return;
    }
    setError('');

    const conditions = form.always ? { always: true } : {
      always: false,
      dateRange: form.dateRange.start && form.dateRange.end ? form.dateRange : null,
      daysOfWeek: form.daysOfWeek.length > 0 ? form.daysOfWeek : null
    };

    const payload = {
      code: form.code.toUpperCase(),
      type: form.type,
      discount: parseFloat(form.discount),
      minOrder: parseFloat(form.minOrder || 0),
      description: form.description,
      usageLimit: form.usageLimit ? parseInt(form.usageLimit) : null,
      expiresAt: form.expiresAt || null,
      conditions,
      applicableItems: form.applicableItems,
      applicableCategories: form.applicableCategories
    };

    try {
      await api.createCoupon(payload);
      await load();
      setShowForm(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create coupon');
    }
  };

  const handleStatusChange = async (coupon) => {
    if (updatingCouponId) return;
    const nextActive = !coupon.active;
    setUpdatingCouponId(coupon.id);
    setStatusError('');
    try {
      const updated = await api.updateCouponStatus(coupon.id, nextActive);
      setCoupons(current => current.map(item => item.id === coupon.id ? updated : item));
    } catch (err) {
      setStatusError(err.response?.data?.detail || 'Failed to update coupon status');
    } finally {
      setUpdatingCouponId(null);
    }
  };

  const toggleDay = (day) => {
    setForm(p => ({
      ...p,
      daysOfWeek: p.daysOfWeek.includes(day)
        ? p.daysOfWeek.filter(d => d !== day)
        : [...p.daysOfWeek, day]
    }));
  };

  const toggleItem = (itemId) => {
    setForm(p => ({
      ...p,
      applicableItems: p.applicableItems.includes(itemId)
        ? p.applicableItems.filter(i => i !== itemId)
        : [...p.applicableItems, itemId]
    }));
  };

  const toggleCategory = (catId) => {
    setForm(p => ({
      ...p,
      applicableCategories: p.applicableCategories.includes(catId)
        ? p.applicableCategories.filter(c => c !== catId)
        : [...p.applicableCategories, catId]
    }));
  };

  if (loading) return <div className="flex justify-center py-16"><div className="w-7 h-7 border-2 border-amber-600 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="admin-view space-y-4">
      <AdminTopbarPortal><div className="admin-topbar-controls">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Coupons</h2>
          <p className="text-gray-500 text-sm">Create and manage discount codes</p>
        </div>
        <button onClick={openForm}
          className="flex items-center gap-2 bg-gray-900 text-white px-4 py-2.5 rounded-xl text-sm font-semibold hover:bg-amber-700 transition-colors">
          <Plus className="w-4 h-4" /> Add Coupon
        </button>
      </div></AdminTopbarPortal>

      {showForm && (
        <div className="admin-drawer-backdrop" onMouseDown={() => { setShowForm(false); setError(''); }}>
        <div className="admin-drawer" onMouseDown={event => event.stopPropagation()} role="dialog" aria-modal="true" aria-label="Create coupon">
          <div className="admin-drawer-heading">
            <div><span>Coupon</span><h3>New coupon</h3></div>
            <button onClick={() => { setShowForm(false); setError(''); }} aria-label="Close coupon editor">
              <X />
            </button>
          </div>

          <div className="space-y-4">
            {/* Code & Type */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Coupon Code *</label>
                <input type="text" placeholder="SAVE10" value={form.code}
                  onChange={e => setForm(p => ({ ...p, code: e.target.value.toUpperCase() }))}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 font-mono" />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Type *</label>
                <select value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 bg-white">
                  <option value="percentage">Percentage Off</option>
                  <option value="fixed">Fixed Amount Off</option>
                  <option value="bogo">BOGO (Buy One Get One Free)</option>
                  <option value="bogo_half">BOGO Half Price</option>
                </select>
              </div>
            </div>

            {/* Discount & Min Order */}
            {(form.type === 'percentage' || form.type === 'fixed') && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">
                    {form.type === 'percentage' ? 'Discount % *' : 'Discount Amount (£) *'}
                  </label>
                  <input type="number" placeholder={form.type === 'percentage' ? '10' : '5.00'} step="0.01" value={form.discount}
                    onChange={e => setForm(p => ({ ...p, discount: e.target.value }))}
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Minimum Order (£)</label>
                  <input type="number" placeholder="0.00" step="0.01" value={form.minOrder}
                    onChange={e => setForm(p => ({ ...p, minOrder: e.target.value }))}
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500" />
                </div>
              </div>
            )}

            {/* Description */}
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Description</label>
              <input type="text" placeholder="10% off your order" value={form.description}
                onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500" />
            </div>

            {/* Usage Limit & Expiry */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Usage Limit</label>
                <input type="number" placeholder="Unlimited" value={form.usageLimit}
                  onChange={e => setForm(p => ({ ...p, usageLimit: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500" />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Expires At</label>
                <input type="date" value={form.expiresAt}
                  onChange={e => setForm(p => ({ ...p, expiresAt: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500" />
              </div>
            </div>

            {/* Conditions */}
            <div className="border-t border-gray-200 pt-4">
              <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Clock className="w-4 h-4" /> Availability Conditions
              </h4>
              
              <label className="flex items-center gap-2 mb-3 cursor-pointer">
                <input type="checkbox" checked={form.always}
                  onChange={e => setForm(p => ({ ...p, always: e.target.checked }))}
                  className="w-4 h-4 rounded accent-amber-600" />
                <span className="text-sm text-gray-700">Always active (no time restrictions)</span>
              </label>

              {!form.always && (
                <div className="space-y-3 pl-6">
                  {/* Date Range */}
                  <div>
                    <label className="text-xs text-gray-500 mb-1.5 block flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5" /> Active Date Range
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <input type="date" placeholder="Start date" value={form.dateRange.start}
                        onChange={e => setForm(p => ({ ...p, dateRange: { ...p.dateRange, start: e.target.value } }))}
                        className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500" />
                      <input type="date" placeholder="End date" value={form.dateRange.end}
                        onChange={e => setForm(p => ({ ...p, dateRange: { ...p.dateRange, end: e.target.value } }))}
                        className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500" />
                    </div>
                  </div>

                  {/* Days of Week */}
                  <div>
                    <label className="text-xs text-gray-500 mb-1.5 block">Active on Days</label>
                    <div className="flex flex-wrap gap-2">
                      {DAYS.map(day => (
                        <button key={day} onClick={() => toggleDay(day)}
                          className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                            form.daysOfWeek.includes(day)
                              ? 'bg-amber-600 text-white'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}>
                          {day.slice(0, 3)}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Applicable To (for BOGO) */}
            {(form.type === 'bogo' || form.type === 'bogo_half') && (
              <div className="border-t border-gray-200 pt-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Tag className="w-4 h-4" /> Apply BOGO To
                </h4>
                <p className="text-xs text-gray-500 mb-3">Leave empty to apply to all items, or select specific items/categories</p>
                
                {/* Categories */}
                <div className="mb-3">
                  <label className="text-xs text-gray-500 mb-1.5 block">Categories</label>
                  <div className="flex flex-wrap gap-2">
                    {categories.map(cat => (
                      <button key={cat.id} onClick={() => toggleCategory(cat.id)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                          form.applicableCategories.includes(cat.id)
                            ? 'bg-amber-600 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}>
                        {cat.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Items */}
                <div>
                  <label className="text-xs text-gray-500 mb-1.5 block">Specific Items</label>
                  <div className="max-h-32 overflow-y-auto border border-gray-200 rounded-lg p-2 space-y-1">
                    {menuItems.map(item => (
                      <label key={item.id} className="flex items-center gap-2 px-2 py-1 hover:bg-gray-50 rounded cursor-pointer">
                        <input type="checkbox" checked={form.applicableItems.includes(item.id)}
                          onChange={() => toggleItem(item.id)}
                          className="w-3.5 h-3.5 rounded accent-amber-600" />
                        <span className="text-xs text-gray-700">{item.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {error && <p className="text-red-500 text-xs mt-3">{error}</p>}
          
          <div className="admin-drawer-footer">
            <button onClick={handleSave}
              className="bg-gray-900 text-white px-6 py-2.5 rounded-xl text-sm font-semibold hover:bg-amber-700 transition-colors">
              Create Coupon
            </button>
            <button onClick={() => { setShowForm(false); setError(''); }}
              className="px-6 py-2.5 text-gray-600 text-sm font-semibold hover:bg-gray-100 rounded-xl transition-colors">
              Cancel
            </button>
          </div>
        </div>
        </div>
      )}

      {/* Coupons List */}
      {statusError && (
        <div className="admin-coupon-status-error" role="alert">{statusError}</div>
      )}
      {coupons.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center">
          <Tag className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <h3 className="font-semibold text-gray-900 mb-1">No coupons yet</h3>
          <p className="text-gray-500 text-sm mb-4">Create your first discount code</p>
          <button onClick={openForm}
            className="bg-gray-900 text-white px-5 py-2 rounded-xl text-sm font-semibold hover:bg-amber-700 transition-colors">
            Add Coupon
          </button>
        </div>
      ) : (
        <div className="admin-coupons-layout">
          <div className="admin-coupons-summary">
            <div><span>Total coupons</span><strong>{coupons.length}</strong></div>
            <div><span>Currently active</span><strong>{coupons.filter(coupon => coupon.active).length}</strong></div>
          </div>
          <div className="admin-coupons-table">
            <div className="admin-coupons-table-head"><span>Coupon</span><span>Offer</span><span>Minimum order</span><span>Usage</span><span>Status</span></div>
            {coupons.map(coupon => (
              <div key={coupon.id} className="admin-coupons-table-row">
                <div className="admin-coupon-identity">
                  <code>{coupon.code}</code>
                  <span>{coupon.description || 'No description'}</span>
                  {coupon.conditions && !coupon.conditions.always && (
                    <small>{coupon.conditions.daysOfWeek?.length ? coupon.conditions.daysOfWeek.map(day => day.slice(0, 3)).join(', ') : 'Date restricted'}</small>
                  )}
                </div>
                <strong className="admin-coupon-offer">
                  {coupon.type === 'percentage' ? `${coupon.discount}% off` : coupon.type === 'fixed' ? `£${Number(coupon.discount || 0).toFixed(2)} off` : coupon.type === 'bogo_half' ? 'BOGO half price' : 'Buy one, get one'}
                </strong>
                <span>{coupon.minOrder > 0 ? `£${Number(coupon.minOrder).toFixed(2)}` : 'None'}</span>
                <span>{coupon.usageLimit ? `${coupon.usedCount || 0} / ${coupon.usageLimit}` : `${coupon.usedCount || 0} / Unlimited`}</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={Boolean(coupon.active)}
                  aria-label={`${coupon.active ? 'Deactivate' : 'Activate'} ${coupon.code}`}
                  disabled={updatingCouponId === coupon.id}
                  onClick={() => handleStatusChange(coupon)}
                  className={`admin-coupon-status-toggle ${coupon.active ? 'is-active' : ''}`}
                >
                  <span className="admin-coupon-status-track" aria-hidden="true"><span /></span>
                  <strong>{updatingCouponId === coupon.id ? 'Saving…' : coupon.active ? 'Active' : 'Inactive'}</strong>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
